import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Paper
} from '@mui/material';
import { 
  useWebSocketConnections, 
  useRealTimeNotifications, 
  useRealTimeAnalytics 
} from '../../hooks/useWebSocket';
import ConnectionStatus from '../ui/ConnectionStatus';

const WebSocketTest: React.FC = () => {
  const { 
    connections, 
    loading: connectionsLoading, 
    error: connectionsError, 
    refetch: refetchConnections 
  } = useWebSocketConnections();
  
  const { 
    notifications, 
    loading: notificationsLoading, 
    error: notificationsError, 
    refetch: refetchNotifications 
  } = useRealTimeNotifications();
  
  const { 
    analytics, 
    loading: analyticsLoading, 
    error: analyticsError, 
    refetch: refetchAnalytics 
  } = useRealTimeAnalytics();
  
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isTestRunning, setIsTestRunning] = useState(false);

  // Добавление результата теста
  const addTestResult = (result: string) => {
    setTestResults(prev => [`${new Date().toLocaleTimeString()}: ${result}`, ...prev]);
  };

  // Тест подключения
  const testConnection = async () => {
    setIsTestRunning(true);
    addTestResult('Начало теста подключения...');
    
    try {
      await refetchConnections();
      addTestResult('✅ Подключение успешно');
    } catch (err) {
      addTestResult(`❌ Ошибка подключения: ${err}`);
    }
    
    setIsTestRunning(false);
  };

  // Тест отправки уведомления
  const testSendNotification = () => {
    addTestResult('📤 Тест отправки уведомлений');
    refetchNotifications();
  };

  // Тест отправки аналитики
  const testSendAnalytics = () => {
    addTestResult('📊 Тест отправки аналитики');
    refetchAnalytics();
  };

  // Тест подписки на виджет
  const testWidgetSubscription = () => {
    addTestResult('📈 Тест подписки на виджеты');
  };

  // Автоматические тесты при загрузке
  useEffect(() => {
    if (connections.length > 0) {
      addTestResult('🔌 WebSocket подключен!');
    }
  }, [connections]);

  // Мониторинг уведомлений
  useEffect(() => {
    if (notifications.length > 0) {
      const lastNotification = notifications[0];
      addTestResult(`🔔 Получено уведомление: ${lastNotification?.type ?? 'unknown'}`);
    }
  }, [notifications]);

  // Мониторинг аналитики
  useEffect(() => {
    if (analytics.length > 0) {
      const lastAnalytic = analytics[0];
      addTestResult(`📊 Получена аналитика: ${lastAnalytic?.type ?? 'unknown'}`);
    }
  }, [analytics]);

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        WebSocket Тестирование
      </Typography>

      <Grid container spacing={3}>
        {/* Статус соединения */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Статус соединения
              </Typography>
              
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <ConnectionStatus variant="detailed" />
              </Box>

                             {(connectionsError || notificationsError || analyticsError) && (
                 <Alert severity="error" sx={{ mb: 2 }}>
                   {connectionsError || notificationsError || analyticsError}
                 </Alert>
               )}

              <Box display="flex" gap={1} flexWrap="wrap">
                                 <Button 
                   variant="contained" 
                   onClick={testConnection}
                   disabled={isTestRunning || connectionsLoading}
                   startIcon={isTestRunning ? <CircularProgress size={16} /> : null}
                 >
                   {isTestRunning ? 'Подключение...' : 'Подключиться'}
                 </Button>
                 
                 <Button 
                   variant="outlined" 
                   onClick={testSendNotification}
                   disabled={notificationsLoading}
                 >
                   Обновить уведомления
                 </Button>
                 
                 <Button 
                   variant="outlined" 
                   onClick={testSendAnalytics}
                   disabled={analyticsLoading}
                 >
                   Обновить аналитику
                 </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Тесты функциональности */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Тесты функциональности
              </Typography>

                             <Box display="flex" flexDirection="column" gap={1}>
                 <Button 
                   variant="outlined" 
                   onClick={testSendNotification}
                   disabled={notificationsLoading}
                   fullWidth
                 >
                   Обновить уведомления
                 </Button>
                 
                 <Button 
                   variant="outlined" 
                   onClick={testSendAnalytics}
                   disabled={analyticsLoading}
                   fullWidth
                 >
                   Обновить аналитику
                 </Button>
                 
                 <Button 
                   variant="outlined" 
                   onClick={testWidgetSubscription}
                   disabled={connectionsLoading}
                   fullWidth
                 >
                   Тест подписки на виджет
                 </Button>
               </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Статистика */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Статистика
              </Typography>
              
                             <Box display="flex" flexDirection="column" gap={1}>
                 <Typography variant="body2">
                   Соединения: {connections.length}
                 </Typography>
                 <Typography variant="body2">
                   Уведомления: {notifications.length}
                 </Typography>
                 <Typography variant="body2">
                   Аналитика: {analytics.length}
                 </Typography>
               </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Лог результатов */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Лог тестирования
              </Typography>
              
              <Paper 
                variant="outlined" 
                sx={{ 
                  maxHeight: 300, 
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5'
                }}
              >
                <List dense>
                  {testResults.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="Нет результатов тестирования" />
                    </ListItem>
                  ) : (
                    testResults.map((result, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemText 
                            primary={result}
                            sx={{ 
                              fontFamily: 'monospace',
                              fontSize: '0.875rem'
                            }}
                          />
                        </ListItem>
                        {index < testResults.length - 1 && <Divider />}
                      </React.Fragment>
                    ))
                  )}
                </List>
              </Paper>

              <Box mt={2}>
                <Button 
                  size="small" 
                  onClick={() => setTestResults([])}
                >
                  Очистить лог
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Детали соединения */}
      <Box mt={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Конфигурация WebSocket
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  URL: {process.env['REACT_APP_WS_URL'] || 'http://localhost:3001'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Namespaces: /notifications, /analytics
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default WebSocketTest; 